import * as z from "zod";

/**
 * Stream wire protocol schemas — channel name enum, session control inputs,
 * server message envelope. Consumed by:
 * - The RPC contracts in `shared/src/rpc/stream.ts` and `shared/src/rpc/chat-widget.ts`
 * - The frontend stream client (`merchant/app-common/src/stream/stream-provider.tsx`,
 *   `web/src/stream/stream-connection.ts`, `chat/widget/...`)
 * - The server's `StreamSessionService` and channel registry
 *
 * Lives in `effect-api-shared` (not `effect-api-stream-schemas`) so the RPC
 * contracts can reference it. The dependency direction is
 * `effect-api-stream-schemas` → `effect-api-shared`.
 *
 * The channel name list is the single source of truth — the channel schema
 * registry in `effect-api-stream-schemas` registers exactly these names.
 */

export const STREAM_CHANNEL_NAMES = [
  "example",
  "tasks",
  "support-ticket-notifications",
  "support-ticket-bulk-actions",
  "support-ticket-ai-activity",
  "user-notifications",
  "widget-conversations",
  "presence",
  // Redo-internal diagnostic twins: identical payloads and registry
  // plumbing, different transport (Redis stream vs notice server).
  "stream-test-redis",
  "stream-test-notice",
] as const;

export const streamChannelNameSchema = z.enum(STREAM_CHANNEL_NAMES);
export type StreamChannelName = z.infer<typeof streamChannelNameSchema>;

// ---------------------------------------------------------------------------
// Session / control protocol
// ---------------------------------------------------------------------------

export const streamSessionIdSchema = z.string();
export type StreamSessionId = z.infer<typeof streamSessionIdSchema>;

export const streamSubscriptionKeySchema = z.string();
export type StreamSubscriptionKey = z.infer<typeof streamSubscriptionKeySchema>;

export const streamSessionPathSchema = z.object({
  sessionId: streamSessionIdSchema,
});
export type StreamSessionPath = z.infer<typeof streamSessionPathSchema>;

export const openStreamSessionResponseSchema = z.object({
  sessionId: streamSessionIdSchema,
});
export type OpenStreamSessionResponse = z.infer<
  typeof openStreamSessionResponseSchema
>;

export const streamControlSuccessSchema = z.object({
  success: z.literal(true),
});
export type StreamControlSuccess = z.infer<typeof streamControlSuccessSchema>;

export const streamSubscribeControlInputSchema = z.object({
  action: z.literal("subscribe"),
  channel: streamChannelNameSchema,
  input: z.unknown().optional(),
  subscriptionKey: streamSubscriptionKeySchema,
});
export type StreamSubscribeControlInput = z.infer<
  typeof streamSubscribeControlInputSchema
>;

export const streamUnsubscribeControlInputSchema = z.object({
  action: z.literal("unsubscribe"),
  subscriptionKey: streamSubscriptionKeySchema,
});
export type StreamUnsubscribeControlInput = z.infer<
  typeof streamUnsubscribeControlInputSchema
>;

export const streamControlInputSchema = z.discriminatedUnion("action", [
  streamSubscribeControlInputSchema,
  streamUnsubscribeControlInputSchema,
]);
export type StreamControlInput = z.infer<typeof streamControlInputSchema>;

// ---------------------------------------------------------------------------
// Server message envelope (SSE wire format)
// ---------------------------------------------------------------------------

export const streamSubscribedMessageSchema = z.object({
  _tag: z.literal("subscribed"),
  subscriptionKey: streamSubscriptionKeySchema,
  channel: streamChannelNameSchema,
});
export type StreamSubscribedMessage = z.infer<
  typeof streamSubscribedMessageSchema
>;

export const streamInitialMessageSchema = z.object({
  _tag: z.literal("initial"),
  subscriptionKey: streamSubscriptionKeySchema,
  channel: streamChannelNameSchema,
  payload: z.unknown(),
});
export type StreamInitialMessage = z.infer<typeof streamInitialMessageSchema>;

export const streamEventMessageSchema = z.object({
  _tag: z.literal("event"),
  subscriptionKey: streamSubscriptionKeySchema,
  channel: streamChannelNameSchema,
  payload: z.unknown(),
});
export type StreamEventMessage = z.infer<typeof streamEventMessageSchema>;

export const streamUnsubscribedMessageSchema = z.object({
  _tag: z.literal("unsubscribed"),
  subscriptionKey: streamSubscriptionKeySchema,
  channel: streamChannelNameSchema,
});
export type StreamUnsubscribedMessage = z.infer<
  typeof streamUnsubscribedMessageSchema
>;

export const streamErrorMessageSchema = z.object({
  _tag: z.literal("error"),
  subscriptionKey: streamSubscriptionKeySchema.optional(),
  channel: streamChannelNameSchema.optional(),
  message: z.string(),
});
export type StreamErrorMessage = z.infer<typeof streamErrorMessageSchema>;

export const streamServerMessageSchema = z.discriminatedUnion("_tag", [
  streamSubscribedMessageSchema,
  streamInitialMessageSchema,
  streamEventMessageSchema,
  streamUnsubscribedMessageSchema,
  streamErrorMessageSchema,
]);
export type StreamServerMessage = z.infer<typeof streamServerMessageSchema>;
